/**
 * Remaining design / install / freight / travel / materials cash-out for the forecast.
 *
 * remaining[bucket] = max(0, proposal+CO budget − commitments)
 * A paid bill or contractor invoice keeps reducing the estimate until QuickBooks
 * has booked it. Dollars already in QuickBooks are not subtracted again.
 * Dated from Project Hub task groups (same schedule family as the 40%/10% AR).
 *
 * Timber and CNC stay on supplier bills (or proposal placeholders until awarded).
 * Non-timber materials (hardware, fasteners, decking) ride as estimated job costs.
 * Overhead stays in monthly burn.
 * Pure module — no firebase imports.
 */

import type {
  ChangeOrder,
  EnhancedDeal,
  GlobalCosts,
  ProjectPLBucket,
  ProposalBuilderData,
  QBExpense,
} from '../types';
import { computeChangeOrderBudgetByBucket, isApprovedChangeOrder } from './contractValue';
import {
  getLastInstallTaskAnchor,
  getLastProductionTaskAnchor,
  getInstallTaskIds,
  isInstallGroupName,
  isProductionGroupName,
  milestoneEffectiveDate,
  taskEffectiveEndDate,
  toDateSafe,
  type MilestoneLike,
  type TaskEndAnchor,
  type TaskGroupLike,
  type TaskScheduleLike,
} from './paymentDueFlags';
import { getProposalBucketBudget, mapQbExpenseToBucket } from './projectPL';
import {
  getEffectiveIncludeInstallForContractDisplay,
  isStructureClientSelected,
} from './proposalPublicGrandTotals';
import type { SupplierBill } from './supplierBills';
import type { PurchaseRequisition } from '../types/purchasing';
import { purchaseCommitmentKind, resolvePurchaseDealId, resolvePurchasePlBucket } from './purchaseBudget';
import { resolveSpendClass } from './purchasingSpendClass';
import { purchaseCoveredBySupplierBill } from './issuedPurchaseForecast';
import { splitSupplierPurchaseCommitment } from './purchaseOrderPurchases';

export const JOB_COST_BUCKETS = ['design', 'install', 'freight', 'travel', 'materials'] as const;
export type JobCostBucket = (typeof JOB_COST_BUCKETS)[number];

export const JOB_COST_BUCKET_LABELS: Record<JobCostBucket, string> = {
  design: 'Design',
  install: 'Install',
  freight: 'Freight',
  travel: 'Travel',
  materials: 'Materials',
};

type DateLike = Date | { toDate?: () => Date } | string | number | null | undefined;

export type JobCostTaskLike = TaskScheduleLike & { startDate?: DateLike };

export interface JobCostForecastSlice {
  dealId: string;
  projectId?: string;
  projectLabel: string;
  projectName?: string;
  bucket: JobCostBucket;
  freightLeg?: 'inbound' | 'outbound';
  amount: number;
  dueDate: Date | null;
  estimated: boolean;
  anchor?: string;
  taskId?: string;
}

/** Install labor submitted from contractor timesheets. */
export type JobCostContractorInvoice = {
  id?: string;
  contractorName: string;
  invoiceNumber?: string;
  status: 'pending_payment' | 'paid' | 'void';
  periodEnd: Date | null;
  lineItems: Array<{ projectId: string; projectName?: string; amount: number }>;
};

export type ContractorInvoiceForecastSlice = {
  invoiceId: string;
  amount: number;
  vendorName: string;
  label: string;
  dealId: string;
  projectId?: string;
  dueDate: Date | null;
};

export interface JobCostForecastInput {
  deals: EnhancedDeal[];
  proposalsByDeal: Record<string, ProposalBuilderData | null | undefined>;
  changeOrders: ChangeOrder[];
  expenses: Array<QBExpense & { id: string }>;
  supplierBills: SupplierBill[];
  projectIdByDealId: Map<string, string>;
  projectNameById: Map<string, string>;
  /** Hub projects already closed out — leftover budget is not future cash. */
  completedProjectIds?: Set<string>;
  /** Issued / purchased POs — Materials-node POs reduce the materials leftover. */
  purchaseRequisitions?: PurchaseRequisition[];
  /** Contractor invoices — unpaid lines are also forecast AP; both reduce install. */
  contractorInvoices?: JobCostContractorInvoice[];
  /** Timber project id → deal id, for invoice lines. */
  dealIdByProjectId?: Map<string, string>;
  tasks: JobCostTaskLike[];
  taskGroups: TaskGroupLike[];
  milestones: MilestoneLike[];
  now?: Date;
}

const DESIGN_MILESTONE_TITLES = ['Design Approval'];
const FREIGHT_OUT_MILESTONE_TITLES = ['Shipment Date', 'Production Complete'];
const INSTALL_MILESTONE_TITLES = ['Installation Complete', 'Delivery Complete'];

function emptyJobBuckets(): Record<JobCostBucket, number> {
  return { design: 0, install: 0, freight: 0, travel: 0, materials: 0 };
}

function moneyish(value: unknown): number {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function qtyOf(quantity: number | undefined): number {
  return quantity && quantity > 0 ? quantity : 1;
}

/** Hardware / fasteners / decking / pegs / bases — proposal materials minus the timber list. */
export function proposalNonTimberMaterialsAmount(
  proposal: ProposalBuilderData | null | undefined,
): number {
  if (!proposal) return 0;
  const phased = !!proposal.phasingEnabled && (proposal.phases?.length || 0) > 0;
  let sum = 0;
  for (const ns of proposal.nodeSets || []) {
    if (!isStructureClientSelected(ns, proposal.structureSelections, phased)) continue;
    const materials = ns.materials;
    const total = moneyish(materials?.total);
    const timber = moneyish(materials?.timberTotal);
    sum += Math.max(0, total - timber) * qtyOf(ns.quantity);
  }
  return Math.round(sum);
}

/**
 * Other-materials spend (hardware, decking, fasteners). A Bill Payment PO on
 * the materials node was stored as category `timber`; that still counts.
 * A timber RFQ award does not — that package is its own AP line.
 */
export function isMaterialsSupplierBill(bill: SupplierBill): boolean {
  if (bill.status === 'void') return false;
  if (bill.source === 'rfq_award' && !bill.purchaseRequisitionId && bill.category === 'timber') {
    return false;
  }
  if (bill.category === 'hardware' || bill.category === 'fasteners' || bill.category === 'tooling') {
    return true;
  }
  if (bill.plBucket !== 'timber') return false;
  return bill.category === 'other' || bill.source === 'purchase_order';
}

/** Full bill amount — paid portions are history, open portions are already their own AP line. */
function billTotal(bill: SupplierBill): number {
  if (bill.status === 'void') return 0;
  return Math.max(0, Math.round(Number(bill.amount) || 0));
}

const SCHEDULE_BUCKETS = ['design', 'install', 'freight', 'travel'] as const;
type ScheduleBucket = (typeof SCHEDULE_BUCKETS)[number];

function isScheduleBucket(bucket: string): bucket is ScheduleBucket {
  return bucket === 'design' || bucket === 'install' || bucket === 'freight' || bucket === 'travel';
}

function purchaseAmount(row: PurchaseRequisition): number {
  const amount =
    row.status === 'purchased' && Number.isFinite(row.purchasedAmount)
      ? (row.purchasedAmount as number)
      : Number(row.amount) || 0;
  return Math.max(0, amount);
}

/** Issued/purchased POs on one job node, split so a purchased PO can overlap QuickBooks. */
function committedPurchasesForBucket(args: {
  dealId: string;
  bucket: 'timber' | ScheduleBucket;
  purchaseRequisitions: PurchaseRequisition[] | undefined;
  supplierBills: SupplierBill[];
}): { open: number; purchased: number } {
  let open = 0;
  let purchased = 0;
  for (const row of args.purchaseRequisitions || []) {
    if (purchaseCommitmentKind(row.status) !== 'committed') continue;
    if (resolveSpendClass(row.costObject) !== 'job') continue;
    if (resolvePurchaseDealId(row) !== args.dealId) continue;
    if (resolvePurchasePlBucket(row) !== args.bucket) continue;
    if (purchaseCoveredBySupplierBill(row.id, args.supplierBills)) continue;
    if (row.status === 'purchased') {
      purchased += purchaseAmount(row);
      continue;
    }
    const split = splitSupplierPurchaseCommitment(row);
    open += split.open;
    purchased += split.purchased;
  }
  return { open: Math.round(open), purchased: Math.round(purchased) };
}

/** Issued/purchased POs on the Materials node for this deal, net of ones that became supplier bills. */
export function committedMaterialsPurchases(args: {
  dealId: string;
  purchaseRequisitions: PurchaseRequisition[] | undefined;
  supplierBills: SupplierBill[];
}): number {
  const split = committedPurchasesForBucket({ ...args, bucket: 'timber' });
  return split.open + split.purchased;
}

function contractorInstallForDeal(args: {
  dealId: string;
  invoices: JobCostContractorInvoice[] | undefined;
  dealIdByProjectId: Map<string, string> | undefined;
}): { paid: number; unpaid: number } {
  let paid = 0;
  let unpaid = 0;
  const deals = args.dealIdByProjectId;
  if (!deals) return { paid, unpaid };
  for (const invoice of args.invoices || []) {
    if (invoice.status === 'void') continue;
    for (const line of invoice.lineItems) {
      if (deals.get(line.projectId) !== args.dealId) continue;
      const amount = Math.max(0, Math.round(Number(line.amount) || 0));
      if (amount <= 0) continue;
      if (invoice.status === 'paid') paid += amount;
      else unpaid += amount;
    }
  }
  return { paid, unpaid };
}

/** Unpaid contractor invoice lines, one cash item per project on the invoice. */
export function contractorInvoiceForecastSlices(args: {
  invoices: JobCostContractorInvoice[] | undefined;
  dealIdByProjectId: Map<string, string> | undefined;
}): ContractorInvoiceForecastSlice[] {
  const deals = args.dealIdByProjectId;
  if (!deals) return [];
  const slices: ContractorInvoiceForecastSlice[] = [];
  for (const invoice of args.invoices || []) {
    if (invoice.status !== 'pending_payment') continue;
    const vendorName = invoice.contractorName.trim() || 'Contractor';
    invoice.lineItems.forEach((line, index) => {
      const dealId = deals.get(line.projectId);
      if (!dealId) return;
      const amount = Math.max(0, Math.round(Number(line.amount) || 0));
      if (amount <= 0) return;
      const projectName = (line.projectName || '').trim();
      const number = (invoice.invoiceNumber || '').trim();
      slices.push({
        invoiceId: invoice.id || `${vendorName}:${index}`,
        amount,
        vendorName,
        label: projectName
          ? `Install invoice — ${projectName}${number ? ` · ${number}` : ''}`
          : `Install invoice${number ? ` · ${number}` : ''}`,
        dealId,
        projectId: line.projectId,
        dueDate: invoice.periodEnd,
      });
    });
  }
  return slices;
}

function globalCostAmount(gc: GlobalCosts | undefined, key: keyof GlobalCosts): number {
  if (!gc) return 0;
  const item = gc[key];
  if (!item || typeof item !== 'object' || Array.isArray(item)) return 0;
  const row = item as { enabled?: boolean; amount?: number };
  if (row.enabled === false) return 0;
  const amount = Number(row.amount);
  return Number.isFinite(amount) ? amount : 0;
}

/** Map an open supplier bill onto a P&L bucket for leftover-budget subtraction. */
export function supplierBillToPlBucket(bill: SupplierBill): ProjectPLBucket {
  if (bill.plBucket) return bill.plBucket;
  if (bill.category === 'freight') return 'freight';
  if (bill.category === 'timber') return 'timber';
  if (bill.category === 'cnc') return 'production';
  return 'unassigned';
}

function openBillRemaining(bill: SupplierBill): number {
  if (bill.status === 'paid' || bill.status === 'void') return 0;
  if ((Number(bill.amount) || 0) <= 0) return 0;
  const total = Number(bill.amount) || 0;
  const terms = bill.paymentTerms?.installments;
  const paid = bill.installmentPayments;
  if (terms && terms.length > 0 && paid) {
    let unpaid = 0;
    terms.forEach((inst, idx) => {
      if (paid[idx]) return;
      unpaid += total * ((Number(inst.percent) || 0) / 100);
    });
    return Math.max(0, Math.round(unpaid));
  }
  return Math.max(0, Math.round(total));
}

function expenseAmountForDeal(
  expense: QBExpense,
  dealId: string,
): number | null {
  const amount = Number(expense.totalAmount) || 0;
  if (expense.splits && expense.splits.length > 0) {
    const slice = expense.splits.find((s) => s.dealId === dealId);
    return slice ? Number(slice.amount) || 0 : null;
  }
  if (expense.matchedDealId && expense.matchedDealId === dealId) return amount;
  return null;
}

function countsTowardJobActual(expense: QBExpense, dealId: string): number {
  if (expense.excluded) return 0;
  if (expense.categorizationStatus === 'pending' && !expense.matchedDealIdOverridden) return 0;
  if (expense.category === 'overhead') return 0;
  const alloc = expenseAmountForDeal(expense, dealId);
  if (alloc == null) return 0;
  if (expense.category === 'unmatched' && !(expense.splits && expense.splits.length > 0)) {
    const explicitlyLinked =
      expense.categorizationStatus === 'categorized' || Boolean(expense.matchedDealId);
    if (!explicitlyLinked) return 0;
  }
  return alloc;
}

/**
 * Proposal bucket budget for the cash line. Same as Project P&L except
 * install / travel are omitted when the structure or signed contract
 * excluded installation (kit-only / "Install Excluded").
 */
export function getJobCostProposalBudget(
  proposal: ProposalBuilderData | null | undefined,
): Record<JobCostBucket, number> {
  const full = getProposalBucketBudget(proposal);
  const leftover: Record<JobCostBucket, number> = {
    design: Math.max(0, Math.round(full.design || 0)),
    install: Math.max(0, Math.round(full.install || 0)),
    freight: Math.max(0, Math.round(full.freight || 0)),
    travel: Math.max(0, Math.round(full.travel || 0)),
    materials: Math.max(0, proposalNonTimberMaterialsAmount(proposal)),
  };
  if (!proposal) return leftover;

  const includeInstall = getEffectiveIncludeInstallForContractDisplay(proposal, true);
  if (!includeInstall) {
    leftover.install = 0;
    leftover.travel = 0;
    return leftover;
  }

  const phased = !!proposal.phasingEnabled && (proposal.phases?.length || 0) > 0;
  const selected = (proposal.nodeSets || []).filter((ns) =>
    isStructureClientSelected(ns, proposal.structureSelections, phased),
  );
  if (selected.length === 0) return leftover;

  let anyInstallIncluded = false;
  for (const ns of selected) {
    if (!ns.excludeInstall) {
      anyInstallIncluded = true;
      continue;
    }
    const qty = ns.quantity || 1;
    leftover.install -= Math.round(
      ((ns.install?.total || 0) + (ns.overhead?.total || 0) + (ns.install?.overheadTotal || 0)) * qty,
    );
    leftover.travel -= Math.round((ns.travel?.total || 0) * qty);
  }
  if (!anyInstallIncluded) {
    leftover.install = 0;
    leftover.travel = 0;
  } else {
    leftover.install = Math.max(0, leftover.install);
    leftover.travel = Math.max(0, leftover.travel);
  }
  return leftover;
}

export function freightBudgetLegs(
  proposal: ProposalBuilderData | null | undefined,
  changeOrders: ChangeOrder[] = [],
): { inbound: number; outbound: number } {
  const budget = getJobCostProposalBudget(proposal);
  const inbound = globalCostAmount(proposal?.globalCosts, 'globalFreightInbound');
  let outbound = Math.max(0, (budget.freight || 0) - inbound);
  for (const co of changeOrders) {
    if (!isApprovedChangeOrder(co)) continue;
    outbound += Number(computeChangeOrderBudgetByBucket(co).freight) || 0;
  }
  return { inbound: Math.max(0, inbound), outbound: Math.max(0, outbound) };
}

/** Non-void timber/CNC awards that absorbed freight in the quote. */
export function freightAbsorbedLegs(
  bills: SupplierBill[],
  dealId: string,
): { inbound: boolean; outbound: boolean } {
  let inbound = false;
  let outbound = false;
  for (const bill of bills) {
    if (bill.dealId !== dealId || bill.status === 'void') continue;
    if (!bill.paymentTerms?.includesFreight) continue;
    if (bill.category === 'timber') inbound = true;
    if (bill.category === 'cnc') outbound = true;
  }
  return { inbound, outbound };
}

/** Split leftover freight after timber inbound / CNC outbound absorption. */
export function remainingFreightLegs(args: {
  leftoverFreight: number;
  inboundBudget: number;
  outboundBudget: number;
  inboundAbsorbed: boolean;
  outboundAbsorbed: boolean;
}): { inbound: number; outbound: number } {
  const absorbedBudget =
    (args.inboundAbsorbed ? args.inboundBudget : 0) + (args.outboundAbsorbed ? args.outboundBudget : 0);
  const leftover = Math.max(0, Math.round(args.leftoverFreight) - Math.round(absorbedBudget));
  if (leftover <= 0) return { inbound: 0, outbound: 0 };
  const inboundBudget = args.inboundAbsorbed ? 0 : Math.max(0, args.inboundBudget);
  const outboundBudget = args.outboundAbsorbed ? 0 : Math.max(0, args.outboundBudget);
  const denom = inboundBudget + outboundBudget;
  if (denom <= 0) return { inbound: 0, outbound: 0 };
  const inbound = Math.round(leftover * (inboundBudget / denom));
  return { inbound, outbound: leftover - inbound };
}

export function spreadAmountAcrossMonths(
  amount: number,
  start: Date,
  end: Date,
  now: Date,
): Array<{ amount: number; dueDate: Date }> {
  const rounded = Math.round(amount);
  if (rounded <= 0) return [];
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const from = new Date(start.getFullYear(), start.getMonth(), 1);
  const to = new Date(end.getFullYear(), end.getMonth(), 1);
  if (to < from) return [];
  const months: Date[] = [];
  for (let d = from; d <= to; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
    if (d >= currentMonthStart) months.push(d);
  }
  if (months.length === 0) return [];
  const base = Math.floor(rounded / months.length);
  let remainder = rounded - base * months.length;
  return months.map((dueDate, i) => {
    const extra = i === months.length - 1 ? remainder : 0;
    if (i === months.length - 1) remainder = 0;
    return { amount: base + extra, dueDate };
  });
}

/**
 * Leftover design / install / freight / travel / materials dollars for one deal.
 * Timber / production / overhead are never returned as job leftovers.
 */
export function remainingJobCostByBucket(args: {
  dealId: string;
  proposal: ProposalBuilderData | null | undefined;
  changeOrders?: ChangeOrder[];
  expenses: Array<QBExpense & { id: string }>;
  supplierBills: SupplierBill[];
  /** Issued / purchased POs — node POs reduce that node's leftover. */
  purchaseRequisitions?: PurchaseRequisition[];
  contractorInvoices?: JobCostContractorInvoice[];
  dealIdByProjectId?: Map<string, string>;
}): Record<JobCostBucket, number> {
  const jobBudget = getJobCostProposalBudget(args.proposal);
  const budget: Record<JobCostBucket, number> = { ...jobBudget };
  for (const co of args.changeOrders || []) {
    if (!isApprovedChangeOrder(co)) continue;
    const byBucket = computeChangeOrderBudgetByBucket(co);
    budget.design += Number(byBucket.design) || 0;
    budget.install += Number(byBucket.install) || 0;
    budget.freight += Number(byBucket.freight) || 0;
    budget.travel += Number(byBucket.travel) || 0;
  }

  const actual = emptyJobBuckets();
  for (const expense of args.expenses) {
    const amount = countsTowardJobActual(expense, args.dealId);
    if (amount <= 0) continue;
    const bucket = expense.plBucket || mapQbExpenseToBucket(expense);
    if (bucket === 'design' || bucket === 'install' || bucket === 'freight' || bucket === 'travel') {
      actual[bucket] += amount;
    }
  }

  const openBills = emptyJobBuckets();
  const paidBills = emptyJobBuckets();
  for (const bill of args.supplierBills) {
    if (bill.dealId !== args.dealId || bill.status === 'void') continue;
    if (isMaterialsSupplierBill(bill)) {
      // Materials has no QuickBooks overlap in this leftover — the bill is the commitment.
      openBills.materials += billTotal(bill);
      continue;
    }
    const bucket = supplierBillToPlBucket(bill);
    if (!isScheduleBucket(bucket)) continue;
    const total = billTotal(bill);
    const open = openBillRemaining(bill);
    openBills[bucket] += open;
    paidBills[bucket] += Math.max(0, total - open);
  }
  const materialsPos = committedPurchasesForBucket({
    dealId: args.dealId,
    bucket: 'timber',
    purchaseRequisitions: args.purchaseRequisitions,
    supplierBills: args.supplierBills,
  });
  openBills.materials += materialsPos.open + materialsPos.purchased;

  const installInvoices = contractorInstallForDeal({
    dealId: args.dealId,
    invoices: args.contractorInvoices,
    dealIdByProjectId: args.dealIdByProjectId,
  });

  const leftover = emptyJobBuckets();
  leftover.materials = Math.max(0, Math.round((budget.materials || 0) - openBills.materials));
  for (const bucket of SCHEDULE_BUCKETS) {
    const pos = committedPurchasesForBucket({
      dealId: args.dealId,
      bucket,
      purchaseRequisitions: args.purchaseRequisitions,
      supplierBills: args.supplierBills,
    });
    const paidCommit = paidBills[bucket]
      + pos.purchased
      + (bucket === 'install' ? installInvoices.paid : 0);
    const openCommit = openBills[bucket]
      + pos.open
      + (bucket === 'install' ? installInvoices.unpaid : 0);
    // Paid commitments overlap QuickBooks. Open ones are still future cash.
    const consumed = Math.max(actual[bucket], paidCommit) + openCommit;
    leftover[bucket] = Math.max(0, Math.round((budget[bucket] || 0) - consumed));
  }
  return leftover;
}

function isDesignGroupName(name: string): boolean {
  const n = (name || '').trim().toLowerCase();
  return n.includes('design') || n.includes('preconstruction');
}

function isProcurementGroupName(name: string): boolean {
  return (name || '').trim().toLowerCase().includes('procurement');
}

function isShipmentGroupName(name: string): boolean {
  return (name || '').trim().toLowerCase().includes('shipment');
}

/**
 * True once every task in the named group(s) is completed.
 * False when there is no matching group or any task is still open — leftover
 * budget keeps rolling forward until that work is actually done.
 */
function taskGroupComplete(
  projectId: string,
  tasks: JobCostTaskLike[],
  taskGroups: TaskGroupLike[],
  pred: (name: string) => boolean,
): boolean {
  const ids = taskIdsInGroups(projectId, tasks, groupIds(projectId, taskGroups, pred));
  if (ids.size === 0) return false;
  return tasks.every((t) => !t.id || !ids.has(t.id) || t.status === 'completed');
}

/**
 * True once every task in the project's Procurement group(s) is completed.
 * False when there is no Procurement group or any task is still open — the
 * materials leftover keeps rolling forward until buying is actually done.
 */
export function procurementGroupComplete(
  projectId: string,
  tasks: JobCostTaskLike[],
  taskGroups: TaskGroupLike[],
): boolean {
  return taskGroupComplete(projectId, tasks, taskGroups, isProcurementGroupName);
}

export function designGroupComplete(
  projectId: string,
  tasks: JobCostTaskLike[],
  taskGroups: TaskGroupLike[],
): boolean {
  return taskGroupComplete(projectId, tasks, taskGroups, isDesignGroupName);
}

export function installGroupComplete(
  projectId: string,
  tasks: JobCostTaskLike[],
  taskGroups: TaskGroupLike[],
): boolean {
  return taskGroupComplete(projectId, tasks, taskGroups, isInstallGroupName);
}

export function shipmentGroupComplete(
  projectId: string,
  tasks: JobCostTaskLike[],
  taskGroups: TaskGroupLike[],
): boolean {
  return taskGroupComplete(projectId, tasks, taskGroups, isShipmentGroupName);
}

function groupIds(
  projectId: string,
  groups: TaskGroupLike[],
  pred: (name: string) => boolean,
): Set<string> {
  return new Set(
    groups
      .filter((g) => g.projectId === projectId && g.id && pred(g.name))
      .map((g) => g.id as string),
  );
}

function taskIdsInGroups(
  projectId: string,
  tasks: JobCostTaskLike[],
  groupIdsSet: Set<string>,
): Set<string> {
  const ids = new Set<string>();
  if (groupIdsSet.size === 0) return ids;
  const projectTasks = tasks.filter((t) => t.projectId === projectId);
  for (const t of projectTasks) {
    if (t.id && t.groupId && groupIdsSet.has(t.groupId)) ids.add(t.id);
  }
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const t of projectTasks) {
      if (!t.id || ids.has(t.id)) continue;
      if (t.parentTaskId && ids.has(t.parentTaskId)) {
        ids.add(t.id);
        expanded = true;
      }
    }
  }
  return ids;
}

function lastEndAmong(
  projectId: string,
  tasks: JobCostTaskLike[],
  allowedIds?: Set<string>,
): TaskEndAnchor | null {
  let best: TaskEndAnchor | null = null;
  for (const t of tasks) {
    if (t.projectId !== projectId) continue;
    if (allowedIds && (!t.id || !allowedIds.has(t.id))) continue;
    const end = taskEffectiveEndDate(t);
    if (!end) continue;
    if (!best || end.getTime() > best.date.getTime()) {
      best = { date: end, taskId: t.id, taskTitle: (t.title || '').trim() || undefined };
    }
  }
  return best;
}

function firstStartAmong(
  projectId: string,
  tasks: JobCostTaskLike[],
  allowedIds?: Set<string>,
): TaskEndAnchor | null {
  let best: TaskEndAnchor | null = null;
  for (const t of tasks) {
    if (t.projectId !== projectId) continue;
    if (allowedIds && (!t.id || !allowedIds.has(t.id))) continue;
    const start = toDateSafe(t.startDate);
    if (!start) continue;
    if (!best || start.getTime() < best.date.getTime()) {
      best = { date: start, taskId: t.id, taskTitle: (t.title || '').trim() || undefined };
    }
  }
  return best;
}

function findProjectMilestone(
  milestones: MilestoneLike[],
  projectId: string,
  titles: string[],
): { date: Date; title: string } | null {
  const want = titles.map((t) => t.trim().toLowerCase());
  for (const title of want) {
    const match = milestones.find((m) => {
      const pid = (m as { projectId?: string }).projectId;
      if (pid && pid !== projectId) return false;
      return (m.title || '').trim().toLowerCase() === title;
    });
    if (!match) continue;
    const date = milestoneEffectiveDate(match);
    if (date) return { date, title: match.title };
  }
  return null;
}

function fmtAnchorDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export interface JobCostDateAnchor {
  date: Date | null;
  estimated: boolean;
  anchor?: string;
  taskId?: string;
}

export function resolveJobCostDate(args: {
  bucket: JobCostBucket;
  freightLeg?: 'inbound' | 'outbound';
  projectId: string;
  tasks: JobCostTaskLike[];
  taskGroups: TaskGroupLike[];
  milestones: MilestoneLike[];
}): JobCostDateAnchor {
  const { projectId, tasks, taskGroups, milestones } = args;

  const fromGroup = (
    pred: (name: string) => boolean,
    mode: 'end' | 'start',
    groupLabel: string,
  ): JobCostDateAnchor | null => {
    const ids = taskIdsInGroups(projectId, tasks, groupIds(projectId, taskGroups, pred));
    const hit = mode === 'start' ? firstStartAmong(projectId, tasks, ids) : lastEndAmong(projectId, tasks, ids);
    if (!hit) return null;
    const verb = mode === 'start' ? 'starts' : 'ends';
    return {
      date: hit.date,
      estimated: true,
      taskId: hit.taskId,
      anchor: hit.taskTitle
        ? `${groupLabel} — ${hit.taskTitle} ${verb} ${fmtAnchorDate(hit.date)}`
        : `${groupLabel} ${verb} ${fmtAnchorDate(hit.date)}`,
    };
  };

  const fromMilestone = (titles: string[]): JobCostDateAnchor | null => {
    const hit = findProjectMilestone(milestones, projectId, titles);
    if (!hit) return null;
    return {
      date: hit.date,
      estimated: true,
      anchor: `${hit.title} ${fmtAnchorDate(hit.date)}`,
    };
  };

  const missing = (): JobCostDateAnchor => ({
    date: null,
    estimated: true,
    anchor: 'no schedule date — counted this month',
  });

  if (args.bucket === 'design') {
    return fromGroup(isDesignGroupName, 'end', 'Design')
      || fromMilestone(DESIGN_MILESTONE_TITLES)
      || missing();
  }

  if (args.bucket === 'materials') {
    return fromGroup(isProcurementGroupName, 'start', 'Procurement')
      || fromGroup(isProductionGroupName, 'start', 'Production')
      || missing();
  }

  if (args.bucket === 'freight' && args.freightLeg === 'inbound') {
    return fromGroup(isProcurementGroupName, 'end', 'Procurement')
      || fromGroup(isDesignGroupName, 'end', 'Design')
      || fromMilestone(DESIGN_MILESTONE_TITLES)
      || missing();
  }

  if (args.bucket === 'freight') {
    const production = getLastProductionTaskAnchor(projectId, tasks, taskGroups);
    if (production) {
      return {
        date: production.date,
        estimated: true,
        taskId: production.taskId,
        anchor: production.taskTitle
          ? `Production — ${production.taskTitle} ends ${fmtAnchorDate(production.date)}`
          : `Production ends ${fmtAnchorDate(production.date)}`,
      };
    }
    return fromGroup((n) => isShipmentGroupName(n) || isProductionGroupName(n), 'end', 'Shipment')
      || fromMilestone(FREIGHT_OUT_MILESTONE_TITLES)
      || missing();
  }

  if (args.bucket === 'travel') {
    const installIds = getInstallTaskIds(projectId, tasks, taskGroups);
    const start = firstStartAmong(projectId, tasks, installIds);
    if (start) {
      return {
        date: start.date,
        estimated: true,
        taskId: start.taskId,
        anchor: start.taskTitle
          ? `Installation — ${start.taskTitle} starts ${fmtAnchorDate(start.date)}`
          : `Installation starts ${fmtAnchorDate(start.date)}`,
      };
    }
  }

  const install = getLastInstallTaskAnchor(projectId, tasks, taskGroups);
  if (install) {
    return {
      date: install.date,
      estimated: true,
      taskId: install.taskId,
      anchor: install.taskTitle
        ? `Installation — ${install.taskTitle} ends ${fmtAnchorDate(install.date)}`
        : `Installation ends ${fmtAnchorDate(install.date)}`,
    };
  }
  return fromGroup(isInstallGroupName, args.bucket === 'travel' ? 'start' : 'end', 'Installation')
    || fromMilestone(INSTALL_MILESTONE_TITLES)
    || missing();
}

function sliceLabel(bucket: JobCostBucket, freightLeg?: 'inbound' | 'outbound'): string {
  if (bucket === 'freight' && freightLeg === 'inbound') return 'Freight inbound';
  if (bucket === 'freight' && freightLeg === 'outbound') return 'Freight outbound';
  return JOB_COST_BUCKET_LABELS[bucket];
}

function designGroupRange(
  projectId: string,
  tasks: JobCostTaskLike[],
  taskGroups: TaskGroupLike[],
): { start: Date; end: Date } | null {
  const ids = taskIdsInGroups(projectId, tasks, groupIds(projectId, taskGroups, isDesignGroupName));
  const start = firstStartAmong(projectId, tasks, ids);
  const end = lastEndAmong(projectId, tasks, ids);
  if (start && end) {
    return start.date <= end.date
      ? { start: start.date, end: end.date }
      : { start: end.date, end: start.date };
  }
  if (end) return { start: end.date, end: end.date };
  if (start) return { start: start.date, end: start.date };
  return null;
}

function fmtMonth(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short' });
}

export function buildJobCostForecastSlices(input: JobCostForecastInput): JobCostForecastSlice[] {
  const slices: JobCostForecastSlice[] = [];
  const now = input.now ?? new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  for (const deal of input.deals) {
    const dealId = deal.id;
    if (!dealId || deal.outcome !== 'won') continue;
    const projectId = input.projectIdByDealId.get(dealId);
    if (!projectId) continue;
    if (input.completedProjectIds?.has(projectId)) continue;

    const dealCos = input.changeOrders.filter((co) => co.dealId === dealId);
    const remaining = remainingJobCostByBucket({
      dealId,
      proposal: input.proposalsByDeal[dealId],
      changeOrders: dealCos,
      expenses: input.expenses,
      supplierBills: input.supplierBills,
      purchaseRequisitions: input.purchaseRequisitions,
      contractorInvoices: input.contractorInvoices,
      dealIdByProjectId: input.dealIdByProjectId,
    });
    // Leftover stops rolling only when the related work is finished — a past
    // schedule date does not mean the budget was spent.
    if (procurementGroupComplete(projectId, input.tasks, input.taskGroups)) {
      remaining.materials = 0;
    }
    if (designGroupComplete(projectId, input.tasks, input.taskGroups)) {
      remaining.design = 0;
    }
    if (installGroupComplete(projectId, input.tasks, input.taskGroups)) {
      remaining.install = 0;
      remaining.travel = 0;
    }
    if (shipmentGroupComplete(projectId, input.tasks, input.taskGroups)) {
      remaining.freight = 0;
    }

    if (JOB_COST_BUCKETS.every((b) => remaining[b] <= 0)) continue;

    const projectName = input.projectNameById.get(projectId);
    const projectLabel = projectName || deal.title || 'Project';
    const legs = freightBudgetLegs(input.proposalsByDeal[dealId], dealCos);
    const absorbed = freightAbsorbedLegs(input.supplierBills, dealId);

    const push = (
      bucket: JobCostBucket,
      amount: number,
      freightLeg?: 'inbound' | 'outbound',
      datedOverride?: JobCostDateAnchor,
    ) => {
      if (amount <= 0) return;
      let dated = datedOverride ?? resolveJobCostDate({
        bucket,
        freightLeg,
        projectId,
        tasks: input.tasks,
        taskGroups: input.taskGroups,
        milestones: input.milestones,
      });
      if (dated.date && dated.date < currentMonthStart) {
        // Unpaid leftover on open work rolls into this month (closed/finished
        // groups already zeroed remaining above).
        const fallbackAnchor = bucket === 'materials'
          ? 'Procurement'
          : bucket === 'design'
            ? 'Design'
            : bucket === 'install' || bucket === 'travel'
              ? 'Installation'
              : 'Shipment';
        dated = {
          date: now,
          estimated: true,
          taskId: dated.taskId,
          anchor: `${(dated.anchor || fallbackAnchor).replace(/ starts /, ' started ')} — carried forward, counted this month`,
        };
      }
      slices.push({
        dealId,
        projectId,
        projectLabel,
        projectName,
        bucket,
        freightLeg,
        amount,
        dueDate: dated.date,
        estimated: true,
        anchor: dated.anchor,
        taskId: dated.taskId,
      });
    };

    const designRange = designGroupRange(projectId, input.tasks, input.taskGroups);
    if (remaining.design > 0 && designRange) {
      const spread = spreadAmountAcrossMonths(remaining.design, designRange.start, designRange.end, now);
      if (spread.length > 0) {
        const first = spread[0].dueDate;
        const last = spread[spread.length - 1].dueDate;
        const anchor = first.getTime() === last.getTime()
          ? `Design · spread ${fmtMonth(first)}`
          : `Design · spread ${fmtMonth(first)}–${fmtMonth(last)}`;
        for (const part of spread) {
          push('design', part.amount, undefined, {
            date: part.dueDate,
            estimated: true,
            anchor,
          });
        }
      } else {
        // Past-only Design range with unfinished design — still cash owed now.
        push('design', remaining.design, undefined, {
          date: now,
          estimated: true,
          anchor: `Design · carried forward, counted this month`,
        });
      }
    } else {
      push('design', remaining.design);
    }
    push('install', remaining.install);
    push('travel', remaining.travel);
    push('materials', remaining.materials);

    const freightParts = remainingFreightLegs({
      leftoverFreight: remaining.freight,
      inboundBudget: legs.inbound,
      outboundBudget: legs.outbound,
      inboundAbsorbed: absorbed.inbound,
      outboundAbsorbed: absorbed.outbound,
    });
    push('freight', freightParts.inbound, 'inbound');
    push('freight', freightParts.outbound, 'outbound');
  }

  return slices;
}

export function jobCostSliceLabel(slice: Pick<JobCostForecastSlice, 'bucket' | 'freightLeg'>): string {
  return sliceLabel(slice.bucket, slice.freightLeg);
}
